import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { toast } from 'react-toastify';
import moment from 'moment';

import api from '../../../services';
import { canManageOrganization } from '../../../utils/userRoles';
import Card from '../../../components/ui/Card';
import StatusBadge from '../../../components/ui/StatusBadge';
import InlineAlert from '../../../components/ui/InlineAlert';

const SALES_EMAIL = 'sales@acelogisticsmanagement.com';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(1),
  },
  planCard: {
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    '&:hover': {
      borderColor: 'var(--brand)',
      boxShadow: '0 0 0 1px var(--brand)',
    },
  },
  planCardActive: {
    borderColor: 'var(--brand)',
    boxShadow: '0 0 0 2px var(--brand)',
    background: 'var(--brand-soft)',
  },
  planCardReadOnly: {
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
  },
  featureRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  progressBar: {
    height: 6,
    borderRadius: 4,
    backgroundColor: 'var(--gray-100)',
    marginTop: 6,
    marginBottom: 2,
  },
  progressFill: {
    borderRadius: 4,
    background: 'var(--bar-color, var(--brand))',
  },
  cancelLink: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    textDecoration: 'underline',
    '&:hover': {
      color: 'var(--danger-strong)',
    },
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    marginBottom: theme.spacing(2),
  },
  dialogError: {
    color: 'var(--danger-strong)',
    fontSize: 13,
    marginTop: 8,
  },
}));

function UsageMeter({ label, used, max }) {
  const classes = useStyles();
  const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0;
  const tone = pct >= 95 ? 'var(--danger-strong)' : pct >= 80 ? 'var(--warning-strong)' : 'var(--brand)';

  return (
    <Box mb={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>
          {used} <span style={{ opacity: 0.5 }}>/ {max}</span>
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        className={classes.progressBar}
        classes={{ bar: classes.progressFill }}
        style={{ '--bar-color': tone }}
      />
    </Box>
  );
}

export default function SubscriptionTab() {
  const classes = useStyles();
  const history = useHistory();
  const canManage = canManageOrganization();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [dialogStep, setDialogStep] = useState('pick');
  const [dialogPlanKey, setDialogPlanKey] = useState(null);
  const [dialogSeats, setDialogSeats] = useState(1);
  const [dialogError, setDialogError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [licenseKey, setLicenseKey] = useState('');
  const [activating, setActivating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [subRes, plansRes] = await Promise.all([
        api.get('/tenant/subscription'),
        api.get('/tenant/subscription/plans'),
      ]);
      setPayload(subRes.data);
      setPlans(Array.isArray(plansRes.data?.plans) ? plansRes.data.plans : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load subscription');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const activateLicense = async () => {
    if (!licenseKey.trim()) return;
    setActivating(true);
    try {
      await api.post('/tenant/subscription/activate-license', { licenseKey: licenseKey.trim() });
      toast.success('License activated! Plan upgraded.');
      setLicenseKey('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'License activation failed');
    } finally {
      setActivating(false);
    }
  };

  const cancelSubscription = async () => {
    if (!canManage) return;
    setCancelling(true);
    try {
      const { data } = await api.patch('/tenant/subscription/cancel');
      setPayload(data);
      setShowCancel(false);
      toast.success('Subscription set to cancel at period end');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  const openPlanDialog = (initialKey, initialSeats) => {
    setDialogPlanKey(initialKey);
    setDialogSeats(initialSeats || 1);
    setDialogStep('pick');
    setDialogError(null);
    setShowPlans(true);
  };

  const dialogPlan = plans.find((p) => p.key === dialogPlanKey) || null;

  const pickDialogPlan = (p) => {
    if (p.contactSalesOnly) return;
    setDialogPlanKey(p.key);
    setDialogSeats(Math.min(dialogSeats || 1, p.maxTeamMembers || 1) || p.maxTeamMembers || 1);
    setDialogStep('seats');
  };

  const adjustDialogSeats = (delta) => {
    if (!dialogPlan) return;
    setDialogSeats((s) => Math.max(1, Math.min(dialogPlan.maxTeamMembers || 999, s + delta)));
  };

  const confirmPlanChange = async () => {
    if (!dialogPlan) return;
    setConfirming(true);
    setDialogError(null);
    try {
      const { data } = await api.patch('/tenant/subscription/plan', {
        planKey: dialogPlan.key,
        seats: dialogSeats,
      });
      setPayload(data);
      setShowPlans(false);
      toast.success('Plan updated');
    } catch (e) {
      const code = e.response?.data?.code;
      if (code === 'TRIAL_LOCKED' || code === 'NO_ACTIVE_SUBSCRIPTION') {
        setDialogError('You need to start a subscription first.');
      } else {
        setDialogError(e.response?.data?.message || 'Plan update failed');
      }
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <LinearProgress />
      </div>
    );
  }

  const planName = payload?.plan?.displayName || payload?.plan?.key || 'Free';
  const planKey = payload?.plan?.key;
  const jobsUsed = payload?.usage?.jobsCreated || 0;
  const jobsMax = payload?.plan?.maxJobsPerMonth || 0;
  const quotesUsed = payload?.usage?.quotationsCreated || 0;
  const quotesMax = payload?.plan?.maxQuotationsPerMonth || 0;
  const teamUsed = payload?.usage?.teamMembers || 0;
  const teamMax = payload?.effectiveSeatLimit || payload?.plan?.maxTeamMembers || 0;
  const periodEnd = payload?.usage?.periodEnd;

  const subStatus = payload?.subscription?.status;
  const trialLocked = !!payload?.trialLocked;
  const isCanceled = subStatus === 'canceled';
  const isPastDue = subStatus === 'past_due';
  const cancelAtPeriodEnd = !!payload?.subscription?.cancelAtPeriodEnd;
  const currentPeriodEnd = payload?.subscription?.currentPeriodEnd;
  const seatLimit = payload?.subscription?.seatLimit;

  const subscribeUrl = `/subscribe?plan=${encodeURIComponent(planKey || '')}&seats=${encodeURIComponent(seatLimit || '')}`;

  return (
    <div className={classes.root}>
      {isCanceled && (
        <InlineAlert
          tone="danger"
          title="Your subscription was canceled."
          action={
            <Button variant="contained" color="primary" disableElevation onClick={() => history.push('/subscribe')}>
              Resubscribe
            </Button>
          }
        >
          Job and quotation creation is paused until you resubscribe.
        </InlineAlert>
      )}

      {!isCanceled && isPastDue && (
        <InlineAlert
          tone="warning"
          title="Your last payment failed."
          action={
            <Button variant="contained" disableElevation onClick={() => history.push(subscribeUrl)}>
              Update payment method
            </Button>
          }
        >
          Update your payment method to keep your subscription active.
        </InlineAlert>
      )}

      {!isCanceled && !trialLocked && cancelAtPeriodEnd && (
        <InlineAlert tone="warning">
          Cancels on{' '}
          <strong>{currentPeriodEnd ? moment(currentPeriodEnd).format('MMM D, YYYY') : 'end of period'}</strong>.
        </InlineAlert>
      )}

      {/* Current plan hero */}
      <Card>
        <Typography className={classes.sectionLabel}>Current plan</Typography>

        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
          <Box>
            <Box display="flex" alignItems="center" style={{ gap: 10, marginBottom: 4 }}>
              <Typography variant="h5" style={{ fontWeight: 700 }}>
                {planName}
              </Typography>
              {!trialLocked && !isCanceled && <StatusBadge tone="success">Active</StatusBadge>}
              {trialLocked && <StatusBadge tone="info">Trial</StatusBadge>}
            </Box>
            <Typography variant="body2" color="textSecondary">
              {currentPeriodEnd
                ? `Renews ${moment(currentPeriodEnd).format('MMM D, YYYY')}`
                : payload?.subscription?.trialEndsAt
                ? `Trial ends ${moment(payload.subscription.trialEndsAt).format('MMM D, YYYY')}`
                : 'No renewal date'}
            </Typography>
          </Box>

          {canManage && trialLocked && (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              style={{ borderRadius: 8, fontWeight: 600 }}
              onClick={() => history.push('/subscribe')}
            >
              Subscribe
            </Button>
          )}

          {canManage && !trialLocked && !isCanceled && (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              style={{ borderRadius: 8, fontWeight: 600 }}
              onClick={() => openPlanDialog(planKey, seatLimit)}
            >
              Change plan
            </Button>
          )}
        </Box>

        {canManage && !trialLocked && !isCanceled && !cancelAtPeriodEnd && (
          <Box mt={2} pt={2} style={{ borderTop: '1px solid var(--border)' }}>
            <button type="button" className={classes.cancelLink} onClick={() => setShowCancel(true)}>
              Cancel subscription
            </button>
          </Box>
        )}
      </Card>

      {/* Usage */}
      <Card>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography className={classes.sectionLabel} style={{ marginBottom: 0 }}>
            Usage this period
          </Typography>
          {periodEnd && (
            <Typography variant="caption" color="textSecondary">
              Resets {moment(periodEnd).format('MMM D')}
            </Typography>
          )}
        </Box>

        <UsageMeter label="Jobs" used={jobsUsed} max={jobsMax} />
        <UsageMeter label="Quotations" used={quotesUsed} max={quotesMax} />
        <UsageMeter label="Team seats" used={teamUsed} max={teamMax} />
      </Card>

      {/* Trial-locked plan comparison (read-only) */}
      {trialLocked && (
        <Card>
          <Typography className={classes.sectionLabel}>Plans</Typography>
          {plans.map((p) => (
            <div key={p.key} className={classes.planCardReadOnly}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    {p.displayName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {p.maxTeamMembers} seats · {p.maxJobsPerMonth} jobs/mo · {p.maxQuotationsPerMonth} quotes/mo
                  </Typography>
                  {Array.isArray(p.featureList) &&
                    p.featureList.map((f) => (
                      <div key={f} className={classes.featureRow}>
                        <CheckCircleIcon style={{ fontSize: 14, color: 'var(--success-strong)' }} />
                        <Typography variant="caption">{f}</Typography>
                      </div>
                    ))}
                </Box>
                {p.contactSalesOnly ? (
                  <Button variant="outlined" size="small" href={`mailto:${SALES_EMAIL}`}>
                    Contact sales
                  </Button>
                ) : (
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    {p.priceMonthly?.usd != null ? `$${p.priceMonthly.usd}/mo` : ''}
                  </Typography>
                )}
              </Box>
            </div>
          ))}
        </Card>
      )}

      {/* License key activation */}
      {canManage && (
        <Card>
          <Typography className={classes.sectionLabel}>Activate license key</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Enter a license key to unlock a plan. Keys are single-use and bound to this organization.
          </Typography>
          <Box display="flex" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="e.g. ACE-ENT-XXXX-XXXX-XXXX"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              style={{ flex: '1 1 260px', fontFamily: 'monospace' }}
              inputProps={{ style: { fontFamily: 'monospace', letterSpacing: '0.05em' } }}
              onKeyDown={(e) => e.key === 'Enter' && activateLicense()}
            />
            <Button
              variant="contained"
              color="primary"
              disableElevation
              disabled={!licenseKey.trim() || activating}
              onClick={activateLicense}
              style={{ borderRadius: 8, fontWeight: 600 }}
            >
              {activating ? 'Activating…' : 'Activate'}
            </Button>
          </Box>
        </Card>
      )}

      {/* Change-plan stepper dialog */}
      <Dialog open={showPlans} onClose={() => setShowPlans(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Change plan
          </Typography>
        </DialogTitle>
        <DialogContent>
          {dialogStep === 'pick' &&
            plans.map((p) => (
              <div
                key={p.key}
                className={`${classes.planCard} ${dialogPlanKey === p.key ? classes.planCardActive : ''}`}
                onClick={() => pickDialogPlan(p)}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                      {p.displayName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {p.maxTeamMembers} seats · {p.maxJobsPerMonth} jobs/mo · {p.maxQuotationsPerMonth} quotes/mo
                    </Typography>
                  </Box>
                  {p.contactSalesOnly ? (
                    <Button
                      variant="outlined"
                      size="small"
                      href={`mailto:${SALES_EMAIL}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Contact sales
                    </Button>
                  ) : (
                    planKey === p.key && <StatusBadge tone="success">Current</StatusBadge>
                  )}
                </Box>
              </div>
            ))}

          {dialogStep === 'seats' && dialogPlan && (
            <Box>
              <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8 }}>
                {dialogPlan.displayName} — seats
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 12 }}>
                <IconButton size="small" onClick={() => adjustDialogSeats(-1)}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography variant="body1" style={{ minWidth: 24, textAlign: 'center' }}>
                  {dialogSeats}
                </Typography>
                <IconButton size="small" onClick={() => adjustDialogSeats(1)}>
                  <AddIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" color="textSecondary">
                  up to {dialogPlan.maxTeamMembers} on this plan
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                You'll be charged the prorated difference today.
              </Typography>
            </Box>
          )}

          {dialogStep === 'confirm' && dialogPlan && (
            <Box>
              <Typography variant="body1" style={{ marginBottom: 8 }}>
                Switch to <strong>{dialogPlan.displayName}</strong> with <strong>{dialogSeats}</strong> seat
                {dialogSeats === 1 ? '' : 's'}?
              </Typography>
              <Typography variant="body2" color="textSecondary">
                You'll be charged the prorated difference today.
              </Typography>
              {dialogError && <Typography className={classes.dialogError}>{dialogError}</Typography>}
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setShowPlans(false)} style={{ borderRadius: 8 }}>
            Close
          </Button>
          {dialogStep === 'seats' && (
            <Button
              color="primary"
              variant="contained"
              disableElevation
              onClick={() => setDialogStep('confirm')}
              style={{ borderRadius: 8 }}
            >
              Continue
            </Button>
          )}
          {dialogStep === 'confirm' && (
            <Button
              color="primary"
              variant="contained"
              disableElevation
              disabled={confirming}
              onClick={confirmPlanChange}
              style={{ borderRadius: 8 }}
            >
              {confirming ? 'Confirming…' : 'Confirm'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <Dialog open={showCancel} onClose={() => setShowCancel(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Cancel subscription?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Your plan stays active until{' '}
            <strong>{currentPeriodEnd ? moment(currentPeriodEnd).format('MMM D, YYYY') : 'end of period'}</strong>.
            After that, job and quotation creation will be paused until you resubscribe.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px', gap: 8 }}>
          <Button
            variant="outlined"
            onClick={() => setShowCancel(false)}
            style={{ borderRadius: 8 }}
            disabled={cancelling}
          >
            Keep subscription
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={cancelSubscription}
            disabled={cancelling}
            style={{ borderRadius: 8, backgroundColor: 'var(--danger-strong)', color: '#fff', fontWeight: 600 }}
          >
            {cancelling ? 'Cancelling…' : 'Yes, cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
